/**
 * Debug logger utility for environment-specific logging
 * Follows AWS PowerTools-like structure but for frontend use
 * Logging is primarily enabled in development mode only
 */

// Determine environment settings
const isProduction = process.env.NODE_ENV === 'production';
const isLocalDevelopment = window.location.hostname === 'localhost';

// Enable logging only in development or when explicitly enabled
const enableLogging = 
  !isProduction || 
  process.env.REACT_APP_ENABLE_LOGGING === 'true' || 
  isLocalDevelopment;

// Log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Default log level based on environment
const defaultLogLevel = isProduction ? LogLevel.ERROR : LogLevel.DEBUG;

// Current log level - can be changed at runtime
let currentLogLevel = defaultLogLevel;

// Correlation ID for request tracking
let correlationId: string | null = null;

/**
 * Set the current log level
 */
export const setLogLevel = (level: LogLevel): void => {
  currentLogLevel = level;
  if (enableLogging) {
    console.debug(`[DEBUG] Log level set to ${LogLevel[level]}`);
  }
};

/**
 * Set correlation ID for request tracking
 */
export const setCorrelationId = (id: string): void => {
  correlationId = id;
};

/**
 * Format log message with timestamp and correlation ID
 */
const formatLogMessage = (level: string, message: string): string => {
  const timestamp = new Date().toISOString();
  const corrId = correlationId ? `[${correlationId}]` : '';
  return `[${timestamp}] [${level}]${corrId} ${message}`;
};

/**
 * Debug level logging - only shown in development
 */
export const debug = (message: string, ...args: any[]): void => {
  if (!enableLogging) return;
  if (currentLogLevel <= LogLevel.DEBUG) {
    console.debug(formatLogMessage('DEBUG', message), ...args);
  }
};

/**
 * Info level logging - only shown in development
 */
export const info = (message: string, ...args: any[]): void => {
  if (!enableLogging) return;
  if (currentLogLevel <= LogLevel.INFO) {
    console.info(formatLogMessage('INFO', message), ...args);
  }
};

/**
 * Warning level logging - only shown in development
 */
export const warn = (message: string, ...args: any[]): void => {
  if (!enableLogging) return;
  if (currentLogLevel <= LogLevel.WARN) {
    console.warn(formatLogMessage('WARN', message), ...args);
  }
};

/**
 * Error level logging
 * In production, only critical errors are logged with minimal information
 */
export const error = (message: string, ...args: any[]): void => {
  if (currentLogLevel <= LogLevel.ERROR) {
    if (enableLogging) {
      // Full error logging in development
      console.error(formatLogMessage('ERROR', message), ...args);
    } else {
      // Minimal error logging in production if absolutely necessary
      // This could be left empty if no logging is desired in production
      // Or could integrate with an error monitoring service instead
    }
  }
};

/**
 * Sanitize sensitive data from logs
 */
export const sanitize = (data: any): any => {
  if (!data) return data;
  
  // Clone the data to avoid modifying the original
  const sanitized = JSON.parse(JSON.stringify(data));
  
  // List of fields to sanitize
  const sensitiveFields = ['password', 'token', 'secret', 'apiKey', 'authorization'];
  
  // Recursively sanitize objects
  const sanitizeObject = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;
    
    Object.keys(obj).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveFields.some(field => lowerKey.includes(field))) {
        obj[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object') {
        sanitizeObject(obj[key]);
      }
    });
  };
  
  sanitizeObject(sanitized);
  return sanitized;
};

/**
 * Log environment information (only in development)
 */
export const logEnvironmentInfo = (): void => {
  if (!enableLogging) return;
  
  info(`Environment: ${process.env.NODE_ENV}`);
  info(`Hostname: ${window.location.hostname}`);
  info(`WebSocket URL: ${process.env.REACT_APP_WS_URL || 'default'}`);
  info(`API URL: ${process.env.REACT_APP_API_URL || 'default'}`);
  info(`Logging Enabled: ${enableLogging}`);
};

// Export a default object for easier imports
export default {
  debug,
  info,
  warn,
  error,
  setLogLevel,
  setCorrelationId,
  sanitize,
  logEnvironmentInfo,
  LogLevel
};